/**
 * Animation variants and utilities for Motion
 * Using Motion (formerly Framer Motion) for smooth, performant animations
 * ENHANCED for more pronounced, delightful animations
 */

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Standard easing curves
 */
export const easings = {
  easeOut: [0.16, 1, 0.3, 1], // Smooth deceleration
  easeIn: [0.7, 0, 0.84, 0], // Smooth acceleration
  easeInOut: [0.65, 0, 0.35, 1], // Smooth both ways
  spring: { type: "spring", stiffness: 260, damping: 20 } as const,
  springBouncy: { type: "spring", stiffness: 400, damping: 17 } as const,
  springGentle: { type: "spring", stiffness: 200, damping: 25 } as const,
} as const;

/**
 * Standard animation durations (in seconds)
 */
export const durations = {
  fast: 0.25,
  normal: 0.45,
  slow: 0.65,
  verySlow: 0.85,
} as const;

/**
 * Fade in animation
 */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: durations.normal, ease: easings.easeOut },
};

/**
 * Slide in from bottom - ENHANCED with scale and spring
 */
export const slideInFromBottom = {
  initial: { y: 50, opacity: 0, scale: 0.92 },
  animate: { y: 0, opacity: 1, scale: 1 },
  exit: { y: 50, opacity: 0, scale: 0.92 },
  transition: easings.springGentle,
};

/**
 * Slide in from right - ENHANCED with scale and spring
 */
export const slideInFromRight = {
  initial: { x: 80, opacity: 0, scale: 0.94 },
  animate: { x: 0, opacity: 1, scale: 1 },
  exit: { x: 80, opacity: 0, scale: 0.94 },
  transition: easings.springGentle,
};

/**
 * Slide in from left - ENHANCED with scale and spring
 */
export const slideInFromLeft = {
  initial: { x: -80, opacity: 0, scale: 0.94 },
  animate: { x: 0, opacity: 1, scale: 1 },
  exit: { x: -80, opacity: 0, scale: 0.94 },
  transition: easings.springGentle,
};

/**
 * Scale fade animation (for modals) - ENHANCED with bigger scale change
 */
export const scaleFade = {
  initial: { scale: 0.85, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.85, opacity: 0 },
  transition: easings.spring,
};

/**
 * Stagger container for list items - ENHANCED with longer delays
 */
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

/**
 * List item animation (for use with staggerContainer) - ENHANCED
 */
export const listItem = {
  initial: { y: 20, opacity: 0, scale: 0.95 },
  animate: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: easings.springGentle,
  },
  exit: {
    x: -30,
    opacity: 0,
    scale: 0.9,
    transition: { duration: durations.fast },
  },
};

/**
 * Expand/collapse height animation - ENHANCED
 */
export const expandCollapse = {
  initial: { height: 0, opacity: 0, scale: 0.95 },
  animate: {
    height: "auto",
    opacity: 1,
    scale: 1,
    transition: easings.springGentle,
  },
  exit: {
    height: 0,
    opacity: 0,
    scale: 0.95,
    transition: { duration: durations.normal, ease: easings.easeIn },
  },
};

/**
 * Slide up animation (for action bars, notifications) - ENHANCED with bounce
 */
export const slideUp = {
  initial: { y: 120, opacity: 0, scale: 0.9 },
  animate: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: easings.springBouncy,
  },
  exit: {
    y: 120,
    opacity: 0,
    scale: 0.9,
    transition: { duration: durations.normal, ease: easings.easeIn },
  },
};

/**
 * Pop in animation (for badges, tooltips) - ENHANCED with rotation
 */
export const popIn = {
  initial: { scale: 0, opacity: 0, rotate: -10 },
  animate: {
    scale: 1,
    opacity: 1,
    rotate: 0,
    transition: easings.springBouncy,
  },
  exit: {
    scale: 0,
    opacity: 0,
    rotate: 10,
    transition: { duration: durations.fast },
  },
};

/**
 * Minimize animation (for compose dialog) - ENHANCED
 */
export const minimize = {
  maximized: {
    scale: 1,
    y: 0,
    opacity: 1,
    transition: easings.spring,
  },
  minimized: {
    scale: 0.85,
    y: 30,
    opacity: 0.9,
    transition: easings.spring,
  },
};

/**
 * Swipe animation (for swipeable list items)
 */
export const swipe = {
  rest: { x: 0 },
  left: { x: -80 },
  right: { x: 80 },
  transition: { duration: durations.fast, ease: easings.easeOut },
};

/**
 * Hover lift animation - ENHANCED with more lift and scale
 */
export const hoverLift = {
  rest: { y: 0, scale: 1 },
  hover: {
    y: -4,
    scale: 1.02,
    transition: { duration: durations.fast, ease: easings.easeOut },
  },
  tap: {
    scale: 0.97,
    y: 0,
    transition: { duration: 0.1 },
  },
};

/**
 * Card hover animation - more dramatic for larger elements
 */
export const cardHover = {
  rest: { y: 0, scale: 1, rotateX: 0 },
  hover: {
    y: -6,
    scale: 1.02,
    transition: easings.spring,
  },
  tap: {
    scale: 0.98,
    y: 1,
    transition: { duration: 0.1 },
  },
};

/**
 * Bounce in animation - very pronounced
 */
export const bounceIn = {
  initial: { scale: 0, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 15,
    },
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

/**
 * Shake animation for errors or attention
 */
export const shake = {
  x: [0, -10, 10, -10, 10, -5, 5, 0],
  transition: { duration: 0.5 },
};

/**
 * Pulse animation for loading or focus
 */
export const pulse = {
  scale: [1, 1.05, 1],
  opacity: [1, 0.8, 1],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

/**
 * Rotate in animation
 */
export const rotateIn = {
  initial: { rotate: -10, scale: 0.9, opacity: 0 },
  animate: { rotate: 0, scale: 1, opacity: 1 },
  exit: { rotate: 10, scale: 0.9, opacity: 0 },
  transition: easings.springBouncy,
};

/**
 * Flip animation
 */
export const flip = {
  initial: { rotateY: 90, opacity: 0 },
  animate: { rotateY: 0, opacity: 1 },
  exit: { rotateY: -90, opacity: 0 },
  transition: { duration: durations.normal, ease: easings.easeOut },
};

/**
 * Zoom in with rotation - dramatic entrance
 */
export const zoomRotate = {
  initial: { scale: 0.5, rotate: -180, opacity: 0 },
  animate: { scale: 1, rotate: 0, opacity: 1 },
  exit: { scale: 0.5, rotate: 180, opacity: 0 },
  transition: { duration: durations.slow, ease: easings.easeOut },
};

/**
 * Slide and fade from top
 */
export const slideInFromTop = {
  initial: { y: -60, opacity: 0, scale: 0.94 },
  animate: { y: 0, opacity: 1, scale: 1 },
  exit: { y: -60, opacity: 0, scale: 0.94 },
  transition: easings.springGentle,
};

/**
 * Button press animation
 */
export const buttonPress = {
  whileTap: {
    scale: 0.95,
    transition: { duration: 0.1 },
  },
};

/**
 * Button hover with glow
 */
export const buttonHover = {
  whileHover: {
    scale: 1.05,
    transition: easings.spring,
  },
  whileTap: {
    scale: 0.95,
    transition: { duration: 0.1 },
  },
};

/**
 * Create a safe animation object that respects reduced motion preference
 */
type AnimationWithAnimate = Record<string, unknown> & {
  animate?: Record<string, unknown>;
};

export const safeAnimation = (animation: AnimationWithAnimate) => {
  const animateState = animation.animate ?? {};
  if (prefersReducedMotion()) {
    return {
      initial: animateState,
      animate: animateState,
      exit: animateState,
      transition: { duration: 0 },
    };
  }
  return animation;
};

/**
 * Wrapper to conditionally disable animations based on user preference
 */
export const withReducedMotion = <T extends Record<string, unknown>>(
  variants: T,
  fallback: Partial<T> = {}
): T => {
  if (prefersReducedMotion()) {
    return fallback as T;
  }
  return variants;
};
